import { blendTarget, roofHeight, TONGUE_TARGETS } from '@/content/tongueTargets';
import { blendShapes, shapeOf, type MouthShape } from '@/ui/tongue3d/TongueScene';

/**
 * Mouth shapes for the Tongue Lab's step-by-step and spot-the-mistake activities: the bunched r
 * (docs/design/articulation-spec.md) built up one part at a time, and the r with one part wrong.
 */

/** How far each part of the bunched r is formed (0 at rest, 1 as in the r). */
export interface RParts {
  /** The middle (dorsum) lifting toward the roof; above 1 it rises further, toward touching. */
  lift: number;
  /** The sides pressing on the back teeth (bracing and the central groove). */
  brace: number;
  /** The jaw and the slight lip rounding. */
  lips: number;
}

/** The bunched r with each part formed as far as `parts` says. */
export function partialR(parts: RParts): MouthShape {
  const rest = TONGUE_TARGETS.rest;
  const r = TONGUE_TARGETS.rBunched;
  const lifted = blendTarget(rest, r, parts.lift);
  const braced = blendTarget(rest, r, parts.brace);
  const mouth = blendShapes(shapeOf('rest'), shapeOf('rBunched'), parts.lips);
  return {
    tongue: {
      dorsum: lifted.dorsum,
      ...(lifted.frontThickness !== undefined ? { frontThickness: lifted.frontThickness } : {}),
      sides: braced.sides,
      ...(braced.bracing ? { bracing: braced.bracing } : {}),
    },
    jaw: mouth.jaw,
    lips: mouth.lips,
  };
}

export const FULL_R: RParts = { lift: 1, brace: 1, lips: 1 };

/** How close the touching mistake comes to the roof (mm): touching, by the drag's measure. */
const TOUCH_GAP_MM = 0.3;
/** How wide the touching mistake's raised middle is (mm, Gaussian width along the tongue). */
const TOUCH_WIDTH_MM = 16;
/** Where the tip-up mistake's raised front ends (mm along the midline, z): the r behind it. */
const TIP_FROM = -30;
const TIP_TO = -19;

/**
 * The r with one part wrong, for spot the mistake. Each changes only that part and only as much
 * as it takes to see it from the side (blind reviews, 2026-10-14: a middle "too low" by 4 mm
 * could not be told from the r, so it is not one of them).
 */
export type Mistake = 'touching' | 'tipUp' | 'lips';

export function mistakeShape(mistake: Mistake): MouthShape {
  const r = partialR(FULL_R);
  switch (mistake) {
    case 'touching': {
      // The middle lifted too far: it closes against the roof (the air would stop). The r's
      // middle raised by a broad smooth bump around its peak until it meets the hard palate,
      // held just under the roof everywhere (the back stays where it is: a closure at the soft
      // palate would be a "g").
      const peak = r.tongue.dorsum.reduce((best, p) => (p[1] > best[1] ? p : best));
      const lift = (roofHeight(peak[0]) ?? peak[1]) - TOUCH_GAP_MM - peak[1];
      const dorsum = r.tongue.dorsum.map(([z, y]) => {
        const raised = y + lift * Math.exp(-(((z - peak[0]) / TOUCH_WIDTH_MM) ** 2));
        const roof = roofHeight(z);
        return [z, roof === undefined ? raised : Math.min(raised, roof - TOUCH_GAP_MM)] as const;
      });
      return { ...r, tongue: { ...r.tongue, dorsum } };
    }
    case 'tipUp': {
      // The tip up against the roof just behind the top teeth (as for an l), the rest of the
      // tongue as in the r: the r's own front points lifted to just under the roof, fading out
      // by TIP_FROM. No points are added (the rig lays the tongue along its midline by length, so
      // a longer front would pull the whole body forward and lower).
      const dorsum = r.tongue.dorsum.map(([z, y]) => {
        const t = Math.min(1, Math.max(0, (z - TIP_FROM) / (TIP_TO - TIP_FROM)));
        const k = t * t * (3 - 2 * t);
        const roof = roofHeight(z);
        return [z, roof === undefined ? y : y + (roof - TOUCH_GAP_MM - y) * k] as const;
      });
      return { ...r, tongue: { ...r.tongue, dorsum } };
    }
    case 'lips':
      // The tongue and jaw as in the r, the lips in a big kiss: rounded hard and pushed far out
      // (over-rounding makes the r sound like a w; a w itself rounds about 8 mm, the r 4 mm).
      return { ...r, lips: { height: 4, width: 8, protrusion: 14 } };
  }
}
